<template>
  <ContentWrap>
    <!-- 订单信息 -->
    <el-descriptions title="订单信息">
      <el-descriptions-item label="订单号: ">{{ formData.orderNo }}</el-descriptions-item>
      <el-descriptions-item label="配送方式: ">
        <dict-tag :type="DICT_TYPE.TRADE_DELIVERY_TYPE" :value="formData.order.deliveryType!" />
      </el-descriptions-item>
      <el-descriptions-item label="订单类型: ">
        <dict-tag :type="DICT_TYPE.TRADE_ORDER_TYPE" :value="formData.order.type!" />
      </el-descriptions-item>
      <el-descriptions-item label="收货人: ">
        {{ formData.order.receiverName }}
      </el-descriptions-item>
      <el-descriptions-item label="买家留言: ">
        {{ formData.order.userRemark }}
      </el-descriptions-item>
      <el-descriptions-item label="订单来源: ">
        <dict-tag :type="DICT_TYPE.TERMINAL" :value="formData.order.terminal!" />
      </el-descriptions-item>
      <el-descriptions-item label="联系电话: ">
        {{ formData.order.receiverMobile }}
      </el-descriptions-item>
      <el-descriptions-item label="商家备注: ">{{ formData.order.remark }}</el-descriptions-item>
      <el-descriptions-item label="支付单号: ">
        {{ formData.order.payOrderId }}
      </el-descriptions-item>
      <el-descriptions-item label="付款方式: ">
        <dict-tag :type="DICT_TYPE.PAY_CHANNEL_CODE" :value="formData.order.payChannelCode!" />
      </el-descriptions-item>
      <el-descriptions-item label="买家: ">{{ formData?.user?.nickname }}</el-descriptions-item>
    </el-descriptions>

    <!-- 服务信息（O2O） -->
    <el-descriptions v-if="isO2oOrder" title="服务信息" :column="3">
      <el-descriptions-item label="履约方式: ">
        {{ fulfillmentTypeLabel(formData.order?.fulfillmentType) }}
      </el-descriptions-item>
      <el-descriptions-item label="服务名称: ">{{ formData.orderItem?.spuName || '-' }}</el-descriptions-item>
      <el-descriptions-item label="机构: ">{{ o2oDetail.institutionName || '-' }}</el-descriptions-item>
      <el-descriptions-item label="师傅: ">{{ o2oDetail.providerName || '-' }}</el-descriptions-item>
      <el-descriptions-item label="预约日期: ">
        {{ o2oDetail.reservation?.appointmentDate || '-' }}
      </el-descriptions-item>
      <el-descriptions-item label="预约时段: ">
        {{ o2oDetail.reservation?.appointmentTime || '-' }}
      </el-descriptions-item>
      <el-descriptions-item label="服务地址: " :span="3">
        {{ o2oDetail.reservation?.address || formData.order?.receiverDetailAddress || '-' }}
      </el-descriptions-item>
    </el-descriptions>

    <!-- 售后信息 -->
    <el-descriptions title="售后信息">
      <el-descriptions-item label="退款编号: ">{{ formData.no }}</el-descriptions-item>
      <el-descriptions-item label="申请时间: ">
        {{ formatDate(formData.createTime) }}
      </el-descriptions-item>
      <el-descriptions-item label="售后类型: ">
        <dict-tag :type="DICT_TYPE.TRADE_AFTER_SALE_TYPE" :value="formData.type!" />
      </el-descriptions-item>
      <el-descriptions-item label="售后方式: ">
        <dict-tag :type="DICT_TYPE.TRADE_AFTER_SALE_WAY" :value="formData.way!" />
      </el-descriptions-item>
      <el-descriptions-item label="退款金额: ">
        <template v-if="formData.way === 30">换货无退款</template>
        <template v-else>
          {{ fenToYuan(formData.refundPrice!) }} 元
          <span class="ml-8px text-12px text-gray-400">（同意后以最终金额为准）</span>
        </template>
      </el-descriptions-item>
      <el-descriptions-item v-if="formData.usePoint && formData.way !== 30" label="使用积分: ">
        {{ formData.usePoint }} 分
        <span class="ml-8px text-12px text-gray-400">退款成功后原路退回</span>
      </el-descriptions-item>
      <el-descriptions-item v-if="formData.pointPrice && formData.way !== 30" label="积分抵扣: ">
        {{ fenToYuan(formData.pointPrice) }} 元
      </el-descriptions-item>
      <el-descriptions-item v-if="formData.givePoint && formData.way !== 30" label="赠送积分: ">
        {{ formData.givePoint }} 分
        <span class="ml-8px text-12px text-gray-400">退款成功后扣回</span>
      </el-descriptions-item>
      <el-descriptions-item label="退款原因: ">{{ formData.applyReason }}</el-descriptions-item>
      <el-descriptions-item label="补充描述: ">
        {{ formData.applyDescription }}
      </el-descriptions-item>
      <el-descriptions-item label="凭证图片: ">
        <el-image
          v-for="(item, index) in formData.applyPicUrls || []"
          :key="index"
          :src="item"
          :preview-src-list="formData.applyPicUrls || []"
          class="mr-10px h-60px w-60px"
          preview-teleported
        />
      </el-descriptions-item>
      <el-descriptions-item
        v-if="(formData.way === 20 || formData.way === 30) && formData.returnName"
        label="退货地址: "
        :span="3"
      >
        {{ formData.returnName }} {{ formData.returnMobile }} /
        {{ formData.returnAreaName || '' }} {{ formData.returnDetailAddress }}
      </el-descriptions-item>
      <el-descriptions-item
        v-if="formData.way === 30 && formData.exchangeLogisticsNo"
        label="换货物流: "
        :span="3"
      >
        {{ formData.exchangeLogisticsNo }}
        <span v-if="formData.exchangeDeliveryTime" class="ml-8px text-12px text-gray-400">
          {{ formatDate(formData.exchangeDeliveryTime) }}
        </span>
      </el-descriptions-item>
    </el-descriptions>

    <!-- 退款状态 -->
    <el-descriptions :column="1" title="退款状态">
      <el-descriptions-item label="退款状态: ">
        <dict-tag :type="DICT_TYPE.TRADE_AFTER_SALE_STATUS" :value="formData.status!" />
      </el-descriptions-item>
      <el-descriptions-item label-class-name="no-colon">
        <!--
          确认退款后状态仍可能停在 40（等支付回调），此时已有 payRefundId，按钮需置灰防重复发起。
          完成后 status=50，展示置灰「已退款」。
        -->
        <el-button
          v-if="showRefundButton"
          type="primary"
          :disabled="refundButtonDisabled"
          :loading="refundSubmitting"
          @click="refund"
        >
          {{ refundButtonText }}
        </el-button>
        <el-button v-if="formData.status === 10" type="primary" @click="agree">同意售后</el-button>
        <el-button v-if="formData.status === 10" type="primary" @click="disagree">
          拒绝售后
        </el-button>
        <el-button v-if="!isO2oOrder && formData.status === 30" type="primary" @click="receive">
          确认收货
        </el-button>
        <el-button v-if="!isO2oOrder && formData.status === 30" type="primary" @click="refuse">
          拒绝收货
        </el-button>
        <el-button
          v-if="!isO2oOrder && formData.way === 30 && formData.status === 41"
          type="warning"
          @click="openExchangeDelivery"
        >
          填写换货物流
        </el-button>
      </el-descriptions-item>
      <el-descriptions-item>
        <template #label><span style="color: red">提醒: </span></template>
        <template v-if="isO2oOrder">
          服务单仅支持退款，无需退货收货流程。<br />
          同意售后后请核实服务是否已取消/未履约，再点击确认退款。<br />
          如有纠纷请走「上门服务 → 纠纷仲裁」，勿与本页退款流程混淆。
        </template>
        <template v-else>
          如果未发货，请点击同意退款给买家。<br />
          如果实际已发货，请主动与买家联系。<br />
          如果订单整体退款后，优惠券和余额会退还给买家.
        </template>
      </el-descriptions-item>
    </el-descriptions>

    <!-- 商品信息 -->
    <el-descriptions :title="isO2oOrder ? '服务明细' : '商品信息'">
      <el-descriptions-item labelClassName="no-colon">
        <el-row :gutter="20">
          <el-col :span="15">
            <el-table v-if="formData.orderItem" :data="[formData.orderItem]" border>
              <el-table-column :label="isO2oOrder ? '服务' : '商品'" prop="spuName" width="auto">
                <template #default="{ row }">
                  {{ row.spuName }}
                  <el-tag
                    v-for="property in row.properties"
                    :key="property.propertyId"
                    class="mr-10px"
                  >
                    {{ property.propertyName }}: {{ property.valueName }}
                  </el-tag>
                </template>
              </el-table-column>
              <el-table-column label="商品原价" prop="price" width="150">
                <template #default="{ row }">{{ fenToYuan(row.price) }} 元</template>
              </el-table-column>
              <el-table-column label="数量" prop="count" width="100" />
              <el-table-column label="合计" prop="payPrice" width="150">
                <template #default="{ row }">{{ fenToYuan(row.payPrice) }} 元</template>
              </el-table-column>
            </el-table>
          </el-col>
          <el-col :span="10" />
        </el-row>
      </el-descriptions-item>
    </el-descriptions>

    <!-- 操作日志 -->
    <el-descriptions title="售后日志">
      <el-descriptions-item labelClassName="no-colon">
        <el-timeline>
          <el-timeline-item
            v-for="saleLog in formData.logs"
            :key="saleLog.id"
            :timestamp="formatDate(saleLog.createTime)"
            placement="top"
          >
            <div class="el-timeline-right-content">
              <span>{{ saleLog.content }}</span>
            </div>
            <template #dot>
              <span
                :style="{ backgroundColor: getUserTypeColor(saleLog.userType ?? 0) }"
                class="dot-node-style"
              >
                {{ getDictLabel(DICT_TYPE.USER_TYPE, saleLog.userType ?? 0)[0] || '系' }}
              </span>
            </template>
          </el-timeline-item>
        </el-timeline>
      </el-descriptions-item>
    </el-descriptions>
  </ContentWrap>

  <!-- 各种操作的弹窗 -->
  <UpdateAuditReasonForm ref="updateAuditReasonFormRef" @success="getDetail" />
  <AfterSaleAgreeForm ref="agreeFormRef" @success="getDetail" />

  <Dialog v-model="exchangeVisible" title="填写换货物流" width="480px">
    <el-form
      ref="exchangeFormRef"
      v-loading="exchangeLoading"
      :model="exchangeForm"
      :rules="exchangeRules"
      label-width="100px"
    >
      <el-form-item label="物流公司" prop="logisticsId">
        <el-select
          v-model="exchangeForm.logisticsId"
          filterable
          class="!w-full"
          placeholder="请选择物流公司"
        >
          <el-option
            v-for="item in exchangeExpressList"
            :key="item.id!"
            :label="item.name"
            :value="item.id!"
          />
        </el-select>
      </el-form-item>
      <el-form-item label="物流单号" prop="logisticsNo">
        <el-input v-model="exchangeForm.logisticsNo" placeholder="请输入换货快递单号" />
      </el-form-item>
    </el-form>
    <template #footer>
      <el-button :disabled="exchangeLoading" type="primary" @click="submitExchangeDelivery">
        确 定
      </el-button>
      <el-button @click="exchangeVisible = false">取 消</el-button>
    </template>
  </Dialog>
</template>
<script lang="ts" setup>
import * as AfterSaleApi from '@/api/mall/trade/afterSale/index'
import * as DeliveryExpressApi from '@/api/mall/trade/delivery/express'
import { fenToYuan } from '@/utils'
import { DICT_TYPE, getDictLabel, getDictObj } from '@/utils/dict'
import { formatDate } from '@/utils/formatTime'
import UpdateAuditReasonForm from '@/views/mall/trade/afterSale/form/AfterSaleDisagreeForm.vue'
import AfterSaleAgreeForm from '@/views/mall/trade/afterSale/form/AfterSaleAgreeForm.vue'
import { useTagsViewStore } from '@/store/modules/tagsView'
import * as O2oOrderApi from '@/api/o2o/order'
import { useMallStandaloneNav } from '@/hooks/web/useMallStandaloneNav'

defineOptions({ name: 'TradeAfterSaleDetail' })

const { t } = useI18n() // 国际化
const message = useMessage() // 消息弹窗
const { params } = useRoute() // 查询参数
const { currentRoute } = useRouter() // 路由
const { pushMall } = useMallStandaloneNav()
const formData = ref<AfterSaleApi.TradeAfterSaleDetailVO>({
  order: {},
  logs: []
} as AfterSaleApi.TradeAfterSaleDetailVO)
const updateAuditReasonFormRef = ref() // 拒绝售后表单 Ref
const agreeFormRef = ref() // 同意售后表单 Ref
const o2oDetail = ref<O2oOrderApi.O2oOrderDetailVO>({ order: {}, items: [] })
const refundSubmitting = ref(false)

/** 等待退款 / 已完成：都展示退款按钮（完成时置灰）；换货单不展示 */
const showRefundButton = computed(
  () =>
    formData.value.way !== 30 &&
    (formData.value.status === 40 || formData.value.status === 50)
)

const exchangeVisible = ref(false)
const exchangeLoading = ref(false)
const exchangeFormRef = ref()
const exchangeExpressList = ref<DeliveryExpressApi.DeliveryExpressVO[]>([])
const exchangeForm = ref<{ logisticsId?: number; logisticsNo: string }>({ logisticsNo: '' })
const exchangeRules = reactive({
  logisticsId: [{ required: true, message: '请选择物流公司', trigger: 'change' }],
  logisticsNo: [{ required: true, message: '请输入物流单号', trigger: 'blur' }]
})
/** 已发起支付退款单，或售后已完成，不可再点 */
const refundButtonDisabled = computed(
  () =>
    formData.value.status === 50 ||
    !!formData.value.payRefundId ||
    refundSubmitting.value
)
const refundButtonText = computed(() => {
  if (formData.value.status === 50) return '已退款'
  if (formData.value.payRefundId) return '退款处理中'
  return '确认退款'
})

const isO2oOrder = computed(
  () =>
    formData.value.order?.fulfillmentType === 'HOME' ||
    formData.value.order?.fulfillmentType === 'STORE'
)

const fulfillmentTypeLabel = (type?: string) => {
  if (type === 'HOME') return '上门服务'
  if (type === 'STORE') return '到店服务'
  return type || '-'
}

const loadO2oDetail = async () => {
  if (!isO2oOrder.value || !formData.value.orderId) return
  o2oDetail.value = await O2oOrderApi.getO2oOrderDetail(formData.value.orderId)
}

/** 获得 userType 颜色 */
const getUserTypeColor = (type: number) => {
  const dict = getDictObj(DICT_TYPE.USER_TYPE, type)
  switch (dict?.colorType) {
    case 'success':
      return '#67C23A'
    case 'info':
      return '#909399'
    case 'warning':
      return '#E6A23C'
    case 'danger':
      return '#F56C6C'
  }
  return '#409EFF'
}

/** 获得详情 */
const getDetail = async () => {
  const id = Number(params.id)
  if (id) {
    const res = await AfterSaleApi.getAfterSale(id)
    // 没有表单信息则关闭页面返回
    if (res == null) {
      message.notifyError('售后订单不存在')
      close()
    }
    formData.value = res
    await loadO2oDetail()
  }
}

/** 同意售后 */
const agree = async () => {
  agreeFormRef.value?.open(formData.value)
}

/** 拒绝售后 */
const disagree = async () => {
  updateAuditReasonFormRef.value?.open(formData.value)
}

/** 确认收货 */
const receive = async () => {
  try {
    // 二次确认
    await message.confirm('是否确认收货？')
    await AfterSaleApi.receive(formData.value.id)
    // 提示成功
    message.success(t('common.success'))
    await getDetail()
  } catch {}
}

/** 拒绝收货 */
const refuse = async () => {
  try {
    // 二次确认
    await message.confirm('是否拒绝收货？')
    await AfterSaleApi.refuse(formData.value.id)
    // 提示成功
    message.success(t('common.success'))
    await getDetail()
  } catch {}
}

/** 确认退款 */
const refund = async () => {
  if (refundButtonDisabled.value) return
  try {
    await message.confirm('是否确认退款？')
    refundSubmitting.value = true
    await AfterSaleApi.refund(formData.value.id)
    message.success(t('common.success'))
    await getDetail()
  } catch {
  } finally {
    refundSubmitting.value = false
  }
}

const openExchangeDelivery = async () => {
  exchangeForm.value = { logisticsId: undefined, logisticsNo: '' }
  if (!exchangeExpressList.value.length) {
    exchangeExpressList.value =
      (await DeliveryExpressApi.getSimpleDeliveryExpressList()) || []
  }
  exchangeVisible.value = true
  nextTick(() => exchangeFormRef.value?.clearValidate())
}

const submitExchangeDelivery = async () => {
  await exchangeFormRef.value?.validate()
  exchangeLoading.value = true
  try {
    await AfterSaleApi.deliveryExchange({
      id: formData.value.id!,
      logisticsId: exchangeForm.value.logisticsId!,
      logisticsNo: exchangeForm.value.logisticsNo.trim()
    })
    message.success(t('common.success'))
    exchangeVisible.value = false
    await getDetail()
  } finally {
    exchangeLoading.value = false
  }
}

const { delView } = useTagsViewStore() // 视图操作
/** 关闭 tag */
const close = () => {
  delView(unref(currentRoute))
  pushMall('/trade/after-sale')
}
onMounted(async () => {
  await getDetail()
})
</script>
<style lang="scss" scoped>
:deep(.el-descriptions) {
  &:not(:nth-child(1)) {
    margin-top: 20px;
  }

  .el-descriptions__title {
    display: flex;
    align-items: center;

    &::before {
      display: inline-block;
      width: 3px;
      height: 20px;
      margin-right: 10px;
      background-color: #409eff;
      content: '';
    }
  }

  .el-descriptions-item__container {
    margin: 0 10px;

    .no-colon {
      margin: 0;

      &::after {
        content: '';
      }
    }
  }
}

// 时间线样式调整
:deep(.el-timeline) {
  margin: 10px 0 0 160px;

  .el-timeline-item__wrapper {
    position: relative;
    top: -20px;

    .el-timeline-item__timestamp {
      position: absolute !important;
      top: 10px;
      left: -150px;
    }
  }

  .el-timeline-right-content {
    display: flex;
    align-items: center;
    min-height: 30px;
    padding: 10px;
    background-color: var(--app-content-bg-color);

    &::before {
      position: absolute;
      top: 10px;
      left: 13px;
      border-color: transparent var(--app-content-bg-color) transparent transparent; /* 尖角颜色，左侧朝向 */
      border-style: solid;
      border-width: 8px; /* 调整尖角大小 */
      content: '';
    }
  }

  .dot-node-style {
    position: absolute;
    left: -5px;
    display: flex;
    width: 20px;
    height: 20px;
    font-size: 10px;
    color: #fff;
    border-radius: 50%;
    justify-content: center;
    align-items: center;
  }
}
</style>
